import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShoppingBasket, ImageOff } from "lucide-react";
import { useState, useEffect } from "react";
import { QuantityInput } from "@/components/QuantityInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuantityModal({ open, onClose, item, onConfirm }) {
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (item) {
      setQuantity(1);
      setImageUrl(null);
      setImageLoading(true);
      
      if (item.variants && item.variants.length > 0) {
        setSelectedVariant(item.variants[0]);
      } else {
        setSelectedVariant(null);
      }

      // Load image if modal is open
      if (item.image_base64) {
        setImageUrl(item.image_base64);
        setImageLoading(false);
      } else {
        // Fetch image
        fetch(`${API}/menu/${item.id}/image`)
          .then(res => res.json())
          .then(data => {
            setImageUrl(data.image_base64);
            setImageLoading(false);
          })
          .catch(() => setImageLoading(false));
      }
    }
  }, [item]);

  if (!item) return null;

  const hasVariants = item.variants && item.variants.length > 0;
  const currentPrice = selectedVariant ? selectedVariant.price : item.price;
  const totalPrice = currentPrice * quantity;

  const handleConfirm = () => {
    onConfirm(item, quantity, selectedVariant);
    setQuantity(1);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add to Basket</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Item Preview */}
          <div className="flex gap-4 mb-6">
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              ) : imageLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-[#1aa59a] rounded-full animate-spin" />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageOff className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">{item.name}</h3>
              <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
            </div>
          </div>

          {/* Variant Selection */}
          {hasVariants && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-2">Choose Option:</h4>
              <div className="space-y-2">
                {item.variants.map((variant) => (
                  <button
                    key={variant.name}
                    onClick={() => setSelectedVariant(variant)}
                    className={`w-full p-3 rounded-lg border-2 flex justify-between items-center transition-all ${
                      selectedVariant?.name === variant.name
                        ? "border-[#1aa59a] bg-[#1aa59a]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`variant-${variant.name}`}
                  >
                    <span className="font-medium">{variant.name}</span>
                    <span className="text-[#1aa59a] font-semibold">£{variant.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity Selector */}
          <div className="flex flex-col items-center gap-1 mb-6">
            <QuantityInput
              value={quantity}
              onChange={setQuantity}
              size="lg"
              testIds={{ dec: "qty-decrease", inc: "qty-increase", input: "qty-input" }}
            />
            <span className="text-[11px] text-gray-400">Tap the number to type a quantity (max 999)</span>
          </div>

          {/* Total */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 flex justify-between items-center">
            <span className="text-gray-600">Total</span>
            <span className="text-2xl font-bold text-[#1aa59a]">£{totalPrice.toFixed(2)}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              className="flex-1 bg-[#1aa59a] hover:bg-[#158a80]"
              data-testid="confirm-add-to-basket"
            >
              <ShoppingBasket className="w-4 h-4 mr-2" />
              Add to Basket
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
